/**
 * JourneyStrip — the six-stage lifecycle band between the meeting header
 * and the tabs (spec §4). Six nodes + connectors; below them, the current
 * stage's expansion: a plain-language line for machine stages, the derived
 * checklist + lock button for stage 5, one collapsed line for Final.
 * Renders from the same status + Realtime/5s-poll the page already has.
 */
import { useEffect, useRef, useState } from "react";
import { Button } from "../../ui/Button";
import { Icon } from "../../ui/icons";
import { t, tVars } from "../../i18n";
import { downloadAudio, exportDocx, exportJson, exportMarkdown } from "../../lib/api";
import { formatWallClock } from "../../lib/timestamps";
import { ChecklistItem, Journey, JourneyStageId } from "../../lib/journey";

const STAGE_LABEL_KEY: Record<JourneyStageId, Parameters<typeof t>[0]> = {
  upload: "journey.stageUpload",
  briefing: "journey.stageBriefing",
  transcription: "journey.stageTranscription",
  analysis: "journey.stageAnalysis",
  review: "journey.stageReview",
  final: "journey.stageFinal",
};

function chipLabel(item: ChecklistItem): string {
  switch (item.id) {
    case "speakers":    return tVars("journey.chipSpeakers", { n: item.count });
    case "mishearings": return tVars("journey.chipMishearings", { n: item.count });
    case "names_terms":
      return item.target === "project_picker"
        ? tVars("journey.chipNamesTermsUnassigned", { n: item.count })
        : tVars("journey.chipNamesTerms", { n: item.count });
    case "summary":
      return item.target === "regenerate_summary"
        ? t("journey.chipSummaryGenerate")
        : t("journey.chipSummary");
    case "integrity":   return t("journey.integrityBroken"); // key added in Task 15 (M2)
    default:            return item.id;
  }
}

interface Props {
  journey: Journey;
  meetingId: string;
  status: string;
  lockedAt: string | null;
  errorMessage: string | null;
  locking: boolean;
  updating: boolean;            // whole-analysis re-run in flight
  canTryAgain: boolean;         // isRecoverableOrphan(meeting, transcript)
  reworking: { startMs: number; endMs: number } | null; // M2; pass null in M1
  lockedDate: string | null;    // pre-formatted lock date for the Final line
  onTryAgain: () => void;
  onLockToggle: () => void;
  onOpenUpdateAnalysis: () => void;
  onOpenReworkSection?: () => void; // M2
  onChecklistClick: (item: ChecklistItem) => void;
  onScrollToBriefingReview: () => void;
}

export function JourneyStrip({
  journey, meetingId, status, lockedAt, errorMessage, locking, updating,
  canTryAgain, reworking, lockedDate, onTryAgain, onLockToggle,
  onOpenUpdateAnalysis, onOpenReworkSection, onChecklistClick,
  onScrollToBriefingReview,
}: Props) {
  const [lockConfirmOpen, setLockConfirmOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportBusy, setExportBusy] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportRef = useRef<HTMLDivElement | null>(null);

  // Close the export dropdown on outside click / Escape.
  useEffect(() => {
    if (!exportOpen) return;
    function onClick(e: MouseEvent) {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) setExportOpen(false);
    }
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") setExportOpen(false); }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, [exportOpen]);

  // Reset the inline lock confirm whenever the pending picture changes.
  useEffect(() => { setLockConfirmOpen(false); }, [journey.pendingCount, lockedAt]);

  if (journey.stages.length === 0) return null;

  const isFinal = !!lockedAt;
  const isReview = !isFinal && journey.currentIdx === 4 && status === "ready";
  const current = journey.stages[journey.currentIdx];

  const runExport = async (kind: string, fn: () => Promise<void>) => {
    setExportBusy(kind); setExportError(null); setExportOpen(false);
    try { await fn(); } catch (e) { setExportError(e instanceof Error ? e.message : String(e)); }
    finally { setExportBusy(null); }
  };

  // ---- Final: one calm collapsed line --------------------------------------
  if (isFinal) {
    return (
      <div className="journey-strip journey-strip--final" data-testid="journey-strip" role="status">
        <Icon name="check" size={14} aria-hidden />
        <span>{tVars("journey.finalLine", { date: lockedDate ?? "" })}</span>
        <div style={{ position: "relative", marginInlineStart: "auto" }} ref={exportRef}>
          <Button variant="secondary" size="sm" icon="download" disabled={exportBusy !== null}
            onClick={() => setExportOpen((o) => !o)} aria-haspopup="true" aria-expanded={exportOpen}>
            {t("meeting.exportButton")} <Icon name="chevron-down" size={12} style={{ marginInlineStart: 4 }} />
          </Button>
          {exportOpen && (
            <div className="journey-export-menu" role="menu" aria-label={t("journey.exportMenuLabel")}>
              {([
                { kind: "markdown", label: t("meeting.exportMd"),    fn: () => exportMarkdown(meetingId) },
                { kind: "docx",     label: t("meeting.exportDocx"),  fn: () => exportDocx(meetingId) },
                { kind: "json",     label: t("meeting.exportJson"),  fn: () => exportJson(meetingId) },
                { kind: "audio",    label: t("meeting.exportAudio"), fn: () => downloadAudio(meetingId) },
              ] as const).map((item) => (
                <button key={item.kind} type="button" role="menuitem" disabled={exportBusy !== null}
                  onClick={() => void runExport(item.kind, item.fn)}>
                  {exportBusy === item.kind ? t("meeting.preparing") : item.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <Button variant="secondary" size="sm" icon="unlock" disabled={locking} onClick={onLockToggle}>
          {locking ? t("journey.unlocking") : t("meeting.unlock")}
        </Button>
        {exportError && <span role="alert" className="error journey-error-detail">{exportError}</span>}
      </div>
    );
  }

  // ---- Expansion content per current-stage state ----------------------------
  let expansion: React.ReactNode = null;
  if (current.state === "error") {
    const headKey = journey.errorStage === "briefing" ? "journey.errorBriefing"
      : journey.errorStage === "transcription" ? "journey.errorTranscription" : "journey.errorAnalysis";
    expansion = (
      <div className="journey-expansion journey-expansion--error">
        <strong>{t(headKey)}</strong>
        {errorMessage && <span className="muted journey-error-detail">{errorMessage}</span>}
        {canTryAgain && (
          <Button variant="secondary" size="sm" icon="refresh" onClick={onTryAgain}>
            {t("journey.tryAgain")}
          </Button>
        )}
      </div>
    );
  } else if (current.state === "needs_you") {
    expansion = (
      <div className="journey-expansion journey-expansion--needs-you">
        <span>{t("journey.briefingNeedsYou")}</span>
        <button type="button" className="journey-link" onClick={onScrollToBriefingReview}>
          {t("journey.briefingReviewCta")}
        </button>
      </div>
    );
  } else if (!isReview) {
    // Machine stage active: one plain-language line + honest duration hint.
    const lines: Partial<Record<JourneyStageId, [Parameters<typeof t>[0], Parameters<typeof t>[0]]>> = {
      briefing:      ["journey.briefingActive", "journey.briefingHint"],
      transcription: ["journey.transcriptionActive", "journey.transcriptionHint"],
      analysis:      ["journey.analysisActive", "journey.analysisHint"],
    };
    const pair = lines[current.id];
    if (pair) {
      expansion = (
        <div className="journey-expansion journey-expansion--machine" role="status" aria-live="polite">
          <span className="journey-spinner" aria-hidden="true" />
          <span>{t(pair[0])}</span>
          <span className="muted">{t(pair[1])}</span>
        </div>
      );
    }
  } else {
    // Stage 5: the checklist + rework offers + the informed lock.
    expansion = (
      <div className="journey-expansion journey-expansion--review">
        {reworking && (
          <div className="journey-reworking" role="status">
            <span className="journey-spinner" aria-hidden="true" />
            {tVars("journey.reworking", {
              from: formatWallClock(null, reworking.startMs),
              to: formatWallClock(null, reworking.endMs),
            })}
          </div>
        )}
        <div className="journey-review-header">
          {journey.pendingCount > 0
            ? tVars("journey.reviewHeader", { n: journey.pendingCount })
            : (<><Icon name="sparkle" size={14} aria-hidden /> {t("journey.reviewAllClear")}</>)}
        </div>
        {journey.checklist.length > 0 && (
          <div className="journey-chips">
            {journey.checklist.map((item) => (
              <button key={item.id} type="button"
                className={`journey-chip${item.id === "integrity" ? " journey-chip--integrity" : ""}`}
                onClick={() => onChecklistClick(item)}>
                {chipLabel(item)}
              </button>
            ))}
          </div>
        )}
        {journey.integrity?.verdict === "suspect" && (
          <div className="journey-integrity-note muted" dir="auto">
            {journey.integrity.reason}
          </div>
        )}
        <div className="journey-offers">
          <span className="muted">{t("journey.reworkOfferLead")}</span>
          {onOpenReworkSection && (
            <button type="button" className="journey-link" onClick={onOpenReworkSection} disabled={!!reworking}>
              {t("journey.reworkSection")}
            </button>
          )}
          <button type="button" className="journey-link" onClick={onOpenUpdateAnalysis} disabled={updating}>
            {updating ? t("journey.updating") : t("journey.updateAnalysis")}
          </button>
        </div>
        <div className="journey-lock-row">
          {lockConfirmOpen ? (
            <>
              <span>{tVars("journey.lockConfirm", { n: journey.pendingCount })}</span>
              <Button variant="primary" size="sm" icon="lock" disabled={locking} title={t("journey.lockTitle")} onClick={onLockToggle}>
                {locking ? t("journey.locking") : t("journey.lockConfirmYes")}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setLockConfirmOpen(false)}>
                {t("common.cancel")}
              </Button>
            </>
          ) : (
            <Button
              variant={journey.pendingCount > 0 ? "secondary" : "primary"} size="sm" icon="lock"
              disabled={locking}
              title={t("journey.lockTitle")}
              onClick={() => (journey.pendingCount > 0 ? setLockConfirmOpen(true) : onLockToggle())}>
              {locking ? t("journey.locking")
                : journey.pendingCount > 0 ? t("journey.lockAnyway") : t("journey.lockClean")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`journey-strip${mobileOpen ? " journey-strip--mobile-open" : ""}`} data-testid="journey-strip">
      {/* Mobile one-liner (CSS shows this only on narrow viewports) */}
      <button type="button" className="journey-mobile" data-testid="journey-mobile"
        onClick={() => setMobileOpen((o) => !o)} aria-expanded={mobileOpen}>
        {tVars("journey.mobileLine", { x: journey.currentIdx + 1, stage: t(STAGE_LABEL_KEY[current.id]) })}
        {isReview && journey.pendingCount > 0 && <> · {tVars("journey.mobileLeft", { n: journey.pendingCount })}</>}
      </button>

      <div className="journey-full">
        <div className="journey-nodes">
          {journey.stages.map((s, i) => (
            <span key={s.id} className="journey-node-wrap">
              {i > 0 && <span className={`journey-connector${i <= journey.currentIdx ? " journey-connector--done" : ""}`} aria-hidden="true" />}
              <span data-testid="journey-node" className={`journey-node journey-node--${s.state}`}>
                <span className="journey-node-mark" aria-hidden="true">
                  {s.state === "done" ? <Icon name="check" size={11} />
                    : s.state === "error" ? <Icon name="x" size={11} />
                    : s.state === "needs_you" ? "‖" : ""}
                </span>
                <span className="journey-node-label">{t(STAGE_LABEL_KEY[s.id])}</span>
              </span>
            </span>
          ))}
        </div>
        {expansion}
      </div>
    </div>
  );
}
